import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BrainCircuit, RefreshCw, Loader2, Sparkles, Layers, Shield, ScrollText,
  MapPin, Tag, Gauge, Play, History, LayoutDashboard, AlertCircle, Zap,
  Pause, PlayCircle, Clock, Plus,
} from "lucide-react";

const STATUS_COLORS = {
  selected:  "bg-emerald-100 text-emerald-800 border-emerald-300",
  scored:    "bg-amber-100 text-amber-800 border-amber-300",
  discarded: "bg-slate-200 text-slate-700 border-slate-300",
  unresolved:"bg-red-100 text-red-800 border-red-300",
  detected:  "bg-blue-100 text-blue-800 border-blue-300",
  duplicate: "bg-purple-100 text-purple-800 border-purple-300",
};

const TRUST_COLORS = {
  A: "bg-emerald-600 text-white",
  B: "bg-amber-500 text-white",
  C: "bg-slate-500 text-white",
};

function ScoreBar({ value }) {
  const v = Math.max(0, Math.min(100, Number(value || 0)));
  const bg = v >= 70 ? "bg-emerald-500" : v >= 45 ? "bg-amber-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2" data-testid="editorial-score-bar">
      <div className="flex-1 h-2 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full ${bg} transition-all`} style={{ width: `${v}%` }} />
      </div>
      <span className="text-xs font-mono font-bold w-8 text-right">{v}</span>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <span data-testid={`editorial-status-${status || "unknown"}`}
          className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border ${cls}`}>
      {status || "—"}
    </span>
  );
}

// ---------------- CANDIDATES TAB ---------------------------------
function CandidatesTab({ locations, sources, categories }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");
  const [detail, setDetail] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [evaluating, setEvaluating] = useState(null);
  const [publishing, setPublishing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterStatus !== "all") params.editorial_status = filterStatus;
      if (filterLocation !== "all") params.location_id = filterLocation;
      const res = await api.adminEditorialCandidates(params);
      setRows(res.items || []);
    } catch (e) {
      toast.error("No pudimos cargar las candidatas");
    } finally { setLoading(false); }
  }, [filterStatus, filterLocation]);

  useEffect(() => { load(); }, [load]);

  const sourceById = useMemo(() => Object.fromEntries((sources || []).map(s => [s.id, s])), [sources]);
  const locationName = useCallback((id) => (locations.find(l => l.id === id) || {}).name || (id || "—"), [locations]);
  const categoryName = useCallback((id) => (categories.find(c => c.id === id) || {}).name || (id || "—"), [categories]);

  const handleEvaluate = async (id) => {
    setEvaluating(id);
    try {
      const res = await api.adminEditorialEvaluate(id);
      toast.success(`Score ${res.score} · ${res.editorial_status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error evaluando");
    } finally { setEvaluating(null); }
  };

  const handlePublishNow = async (id, title) => {
    if (!window.confirm(`¿Publicar AHORA la candidata "${title || id}"?\n\nSe saltará el score automático pero seguirán los validadores técnicos (cuerpo mínimo, imagen, slug).`)) return;
    setPublishing(id);
    try {
      const res = await api.adminEditorialPublishNow(id, { override_quota: true });
      if (res?.ok) {
        toast.success(`Publicada: ${res.title || res.slug}`);
      } else {
        toast.error(res?.message || "No se pudo publicar");
      }
      load();
    } catch (e) {
      const err = e?.response?.data?.detail?.error;
      const msg = err?.message || e?.response?.data?.detail || "Error publicando";
      toast.error(msg);
    } finally { setPublishing(null); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.adminEditorialSeedCases();
      toast.success(`Semilla creada: ${res.seeded} casos`);
      // Auto-evaluate all
      for (const id of res.ids || []) {
        try { await api.adminEditorialEvaluate(id); } catch { /* ignore per-item errors */ }
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo sembrar");
    } finally { setSeeding(false); }
  };

  return (
    <div className="space-y-4" data-testid="editorial-candidates-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-52" data-testid="editorial-filter-status">
            <SelectValue placeholder="Estado editorial" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="detected">Detectada</SelectItem>
            <SelectItem value="scored">Puntuada</SelectItem>
            <SelectItem value="selected">Seleccionada</SelectItem>
            <SelectItem value="discarded">Descartada</SelectItem>
            <SelectItem value="unresolved">No resuelta</SelectItem>
            <SelectItem value="duplicate">Duplicada</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterLocation} onValueChange={setFilterLocation}>
          <SelectTrigger className="w-52" data-testid="editorial-filter-location">
            <SelectValue placeholder="Ubicación" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las ubicaciones</SelectItem>
            {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="outline" onClick={load} disabled={loading} data-testid="editorial-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Recargar</span>
        </Button>
        <Button onClick={handleSeed} disabled={seeding} data-testid="editorial-seed-cases-btn">
          {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Cargar 6 casos QA
        </Button>
      </div>

      <div className="border border-softblue overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[38%]">Título</TableHead>
              <TableHead>Ubicación</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Trust</TableHead>
              <TableHead className="w-32">Score</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="text-center py-6 text-textgray">Cargando…</TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-6 text-textgray">
                No hay candidatas. Usá <b>Cargar 6 casos QA</b> para probar el motor.
              </TableCell></TableRow>
            )}
            {!loading && rows.map(r => {
              const src = sourceById[r.source_id];
              const trust = src?.trust_level || "B";
              const title = r.generated_title || r.original_title || "(sin título)";
              return (
                <TableRow key={r.id} data-testid={`editorial-row-${r.id}`}>
                  <TableCell className="font-medium max-w-[420px] truncate" title={title}>{title}</TableCell>
                  <TableCell><span className="text-xs">{locationName(r.location_id) || "—"}</span></TableCell>
                  <TableCell><span className="text-xs">{categoryName(r.main_category_id) || r.generated_category || "—"}</span></TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 text-[11px] font-bold ${TRUST_COLORS[trust] || TRUST_COLORS.B}`}>{trust}</span>
                  </TableCell>
                  <TableCell><ScoreBar value={r.editorial_score} /></TableCell>
                  <TableCell><StatusBadge status={r.editorial_status} /></TableCell>
                  <TableCell className="text-[11px] text-textgray max-w-[200px] truncate" title={r.decision_reason}>{r.decision_reason || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="default" className="mr-1 bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => handlePublishNow(r.id, title)}
                            disabled={publishing === r.id || r.editorial_status === "published"}
                            data-testid={`editorial-publish-now-${r.id}`}>
                      {publishing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                      <span className="ml-1">Publicar ahora</span>
                    </Button>
                    <Button size="sm" variant="outline" className="mr-1"
                            onClick={() => handleEvaluate(r.id)}
                            disabled={evaluating === r.id}
                            data-testid={`editorial-evaluate-${r.id}`}>
                      {evaluating === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gauge className="w-3 h-3" />}
                      <span className="ml-1">Reevaluar</span>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDetail(r)} data-testid={`editorial-detail-${r.id}`}>
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="editorial-candidate-detail">
          <DialogHeader>
            <DialogTitle className="font-serif">{detail?.generated_title || detail?.original_title}</DialogTitle>
            <DialogDescription className="text-xs text-textgray">
              Detalle editorial de la candidata, desglose de score y decisiones por canal.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline"><MapPin className="w-3 h-3 mr-1" />{locationName(detail.location_id) || "—"}</Badge>
                <Badge variant="outline"><Tag className="w-3 h-3 mr-1" />{categoryName(detail.main_category_id) || detail.generated_category || "—"}</Badge>
                <StatusBadge status={detail.editorial_status} />
                <span className="text-xs text-textgray">Score: <b>{detail.editorial_score ?? "—"}</b></span>
              </div>
              {detail.score_breakdown && (
                <div className="bg-lightgray/60 p-3 border border-softblue">
                  <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-1">Desglose</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                    {Object.entries(detail.score_breakdown).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-textgray">{k}</span>
                        <span className={Number(v) < 0 ? "text-red-600" : "text-emerald-700"}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {detail.decision_reason && (
                <div className="text-xs italic text-textgray">Motivo: {detail.decision_reason}</div>
              )}
              {detail.generated_summary && <p className="text-sm">{detail.generated_summary}</p>}
              <div className="grid grid-cols-3 gap-2">
                {["portal_decision", "facebook_decision", "instagram_decision"].map(k => (
                  <div key={k} className="border border-softblue p-2">
                    <div className="text-[10px] uppercase tracking-widest text-textgray">{k.replace("_decision", "")}</div>
                    <div className="text-sm font-semibold">{detail[k] || "pending"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Small reusable numeric field kept OUTSIDE ConfigTab to avoid React re-mounting
// its <input> on each parent render.
function NumberField({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 text-sm">
      <span className="text-textgray">{label}</span>
      <Input type="number" className="w-24 font-mono" value={value ?? 0} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}

// ---------------- Publication Slots (franjas horarias editables) --------
function PublicationSlotsCard() {
  const [slots, setSlots] = useState(null);
  const [nextRun, setNextRun] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.adminGetPublicationSlots();
      setSlots(r.slots || []);
    } catch (e) {
      toast.error("No se pudieron cargar las franjas");
    }
    try {
      const nr = await api.publicNextRun();
      setNextRun(nr);
    } catch (_e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addSlot = () => {
    setSlots((cur) => [...(cur || []), { time: "12:00" }].sort((a, b) => a.time.localeCompare(b.time)));
  };
  const removeSlot = (i) => setSlots((cur) => cur.filter((_, idx) => idx !== i));
  const changeSlot = (i, time) => setSlots((cur) => cur.map((s, idx) => idx === i ? { ...s, time } : s));

  const dupes = useMemo(() => {
    const seen = new Set(); const dup = new Set();
    (slots || []).forEach((s) => { if (seen.has(s.time)) dup.add(s.time); else seen.add(s.time); });
    return dup;
  }, [slots]);
  const invalid = useMemo(() => (slots || []).filter((s) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(s.time)), [slots]);

  const canSave = slots && slots.length > 0 && dupes.size === 0 && invalid.length === 0;

  const save = async () => {
    if (!canSave) return;
    const sorted = [...slots].sort((a, b) => a.time.localeCompare(b.time));
    const summary = sorted.map((s) => s.time).join(", ");
    if (!window.confirm(`¿Guardar franjas de publicación?\n\nNuevas: ${summary}\n\nEl scheduler leerá esta configuración inmediatamente y las próximas tandas se ejecutarán a estos horarios (America/Argentina/Ushuaia).`)) return;
    setSaving(true);
    try {
      const r = await api.adminPatchPublicationSlots(sorted);
      setSlots(r.slots || sorted);
      toast.success("Franjas actualizadas");
      const nr = await api.publicNextRun();
      setNextRun(nr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="publication-slots-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock className="w-4 h-4" /> Franjas de publicación
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs text-textgray">
          Los horarios se ejecutan en zona horaria <span className="font-mono">America/Argentina/Ushuaia</span>.
          Cambios se aplican al scheduler real inmediatamente al guardar.
        </div>
        {slots === null ? (
          <div className="text-xs text-textgray">Cargando…</div>
        ) : (
          <div className="space-y-2">
            {slots.map((s, i) => {
              const isDup = dupes.has(s.time);
              const isInv = !/^([01]\d|2[0-3]):[0-5]\d$/.test(s.time);
              return (
                <div key={i} className="flex items-center gap-2" data-testid={`slot-row-${i}`}>
                  <Input
                    type="time"
                    value={s.time}
                    onChange={(e) => changeSlot(i, e.target.value)}
                    className={`w-32 font-mono ${isDup || isInv ? "border-red-500" : ""}`}
                    data-testid={`slot-time-${i}`}
                  />
                  <Button variant="outline" size="sm" onClick={() => removeSlot(i)} data-testid={`slot-remove-${i}`}
                    className="border-red-300 text-red-700 hover:bg-red-50">
                    Eliminar
                  </Button>
                  {isDup && <span className="text-xs text-red-600">Duplicado</span>}
                  {isInv && <span className="text-xs text-red-600">Inválido</span>}
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addSlot} data-testid="slot-add"
              className="gap-1"><Plus className="w-3 h-3" /> Agregar franja</Button>
          </div>
        )}
        {nextRun && nextRun.scheduled_at && (
          <div className="bg-turquoise/10 border border-turquoise/30 p-2 text-xs" data-testid="next-run-preview">
            <div className="uppercase tracking-widest text-textgray text-[10px]">Próxima tanda</div>
            <div className="font-mono text-blueblack">
              {new Date(nextRun.scheduled_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}
              {nextRun.type ? <span className="text-textgray"> · {nextRun.type}</span> : null}
            </div>
          </div>
        )}
        <div className="pt-1">
          <Button onClick={save} disabled={!canSave || saving} data-testid="slots-save"
            className="bg-blueblack text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Guardar cambios
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------- Weather Widget config -------------------------------
function WeatherWidgetCard() {
  const [visible, setVisible] = useState(null);   // ordered fields
  const [available, setAvailable] = useState([]); // {key,label}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.adminGetWeatherWidget();
      setVisible(r.widget?.fields || []);
      setAvailable(r.available_fields || []);
    } catch (_e) { toast.error("No se pudo cargar la configuración del clima"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (key) => setVisible((cur) =>
    (cur || []).includes(key)
      ? cur.filter((f) => f !== key)
      : [...(cur || []), key]
  );
  const move = (idx, delta) => setVisible((cur) => {
    if (!cur) return cur;
    const j = idx + delta;
    if (j < 0 || j >= cur.length) return cur;
    const next = [...cur];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.adminPatchWeatherWidget(visible);
      setVisible(r.widget?.fields || visible);
      toast.success("Widget de clima actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  if (visible === null) return null;

  return (
    <Card data-testid="weather-widget-card">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <LayoutDashboard className="w-4 h-4" /> Widget del clima — Datos visibles
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-xs text-textgray">
          Elegí qué datos muestra la barra meteorológica del portal.
          Ciudad e icono siempre están visibles. Los cambios se aplican inmediatamente.
        </div>
        <div className="space-y-2">
          {/* Visible fields with order controls */}
          {visible.length > 0 && (
            <div className="border border-softblue rounded p-2 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-textgray">Visibles (en orden)</div>
              {visible.map((key, i) => {
                const meta = available.find((a) => a.key === key);
                return (
                  <div key={key} className="flex items-center justify-between gap-2 py-1"
                    data-testid={`wx-visible-${key}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs w-5 text-textgray tabular-nums">{i + 1}.</span>
                      <span className="text-sm">{meta?.label || key}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => move(i, -1)}
                        disabled={i === 0} data-testid={`wx-up-${key}`}>↑</Button>
                      <Button size="sm" variant="outline" onClick={() => move(i, 1)}
                        disabled={i === visible.length - 1} data-testid={`wx-down-${key}`}>↓</Button>
                      <Button size="sm" variant="outline" onClick={() => toggle(key)}
                        data-testid={`wx-toggle-${key}`}
                        className="border-red-300 text-red-700 hover:bg-red-50">Quitar</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {/* Available but not-yet-visible */}
          {available.filter((a) => !visible.includes(a.key)).length > 0 && (
            <div className="border border-dashed border-softblue rounded p-2 space-y-1">
              <div className="text-[10px] uppercase tracking-widest text-textgray">Disponibles</div>
              {available.filter((a) => !visible.includes(a.key)).map((a) => (
                <div key={a.key} className="flex items-center justify-between gap-2 py-1">
                  <span className="text-sm">{a.label}</span>
                  <Button size="sm" variant="outline" onClick={() => toggle(a.key)}
                    data-testid={`wx-toggle-${a.key}`}>Agregar</Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <Button onClick={save} disabled={saving} data-testid="wx-save"
          className="bg-blueblack text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          Guardar cambios
        </Button>
      </CardContent>
    </Card>
  );
}





// ---------------- CONFIG TAB ---------------------------------
function ConfigTab({ config, onSaved }) {
  const [draft, setDraft] = useState(config);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(config); }, [config]);

  const patchScalar = (path, value) => {
    setDraft(prev => ({ ...prev, [path]: Number(value) || 0 }));
  };
  const patchDict = (top, key, value) => {
    setDraft(prev => ({ ...prev, [top]: { ...(prev[top] || {}), [key]: Number(value) || 0 } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.adminEditorialConfigPut(draft);
      toast.success("Configuración editorial guardada");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos guardar");
    } finally { setSaving(false); }
  };

  if (!draft) return null;

  return (
    <div className="space-y-4" data-testid="editorial-config-tab">
      <PublicationSlotsCard />
      <WeatherWidgetCard />
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><MapPin className="w-4 h-4" /> Prioridad geográfica</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(draft.location_weights || {}).map(([k, v]) => (
              <NumberField key={k} label={k} value={v} onChange={(x) => patchDict("location_weights", k, x)} testid={`editorial-cfg-loc-${k}`} />
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Tag className="w-4 h-4" /> Prioridad temática</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-96 overflow-y-auto">
            {Object.entries(draft.category_weights || {}).map(([k, v]) => (
              <NumberField key={k} label={k} value={v} onChange={(x) => patchDict("category_weights", k, x)} testid={`editorial-cfg-cat-${k}`} />
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4" /> Peso de fuente (A/B/C)</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(draft.source_level_weights || {}).map(([k, v]) => (
              <NumberField key={k} label={`Fuente ${k}`} value={v} onChange={(x) => patchDict("source_level_weights", k, x)} testid={`editorial-cfg-src-${k}`} />
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Límites por canal</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(draft.limits || {}).map(([k, v]) => (
              <NumberField key={k} label={k} value={v} onChange={(x) => patchDict("limits", k, x)} testid={`editorial-cfg-limit-${k}`} />
            ))}
            <div className="border-t pt-2 mt-2 space-y-2">
              <NumberField label="own_content_bonus" value={draft.own_content_bonus}
                          onChange={(x) => patchScalar("own_content_bonus", x)} testid="editorial-cfg-own-bonus" />
              <NumberField label="corroboration_bonus" value={draft.corroboration_bonus}
                          onChange={(x) => patchScalar("corroboration_bonus", x)} testid="editorial-cfg-corrob" />
              <NumberField label="saturation_penalty" value={draft.saturation_penalty}
                          onChange={(x) => patchScalar("saturation_penalty", x)} testid="editorial-cfg-sat" />
              <NumberField label="age_penalty_per_day" value={draft.age_penalty_per_day}
                          onChange={(x) => patchScalar("age_penalty_per_day", x)} testid="editorial-cfg-age" />
              <NumberField label="duplicate_penalty" value={draft.duplicate_penalty}
                          onChange={(x) => patchScalar("duplicate_penalty", x)} testid="editorial-cfg-dup" />
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="editorial-cfg-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}

// ---------------- SOURCES TAB ---------------------------------
function SourcesTab({ sources, onChanged }) {
  const [updating, setUpdating] = useState(null);

  const setTrust = async (id, trust_level) => {
    setUpdating(id);
    try {
      await api.adminEditorialSourceTrust(id, { trust_level });
      toast.success("Trust actualizado");
      onChanged?.();
    } catch (e) {
      toast.error("No pudimos actualizar");
    } finally { setUpdating(null); }
  };
  const setInterval = async (id, scrape_interval_minutes, trust_level) => {
    setUpdating(id);
    try {
      await api.adminEditorialSourceTrust(id, { trust_level, scrape_interval_minutes });
      toast.success("Frecuencia actualizada");
      onChanged?.();
    } catch (e) {
      toast.error("No pudimos actualizar");
    } finally { setUpdating(null); }
  };

  return (
    <div className="border border-softblue overflow-x-auto" data-testid="editorial-sources-tab">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fuente</TableHead>
            <TableHead>URL</TableHead>
            <TableHead>Trust</TableHead>
            <TableHead>Intervalo (min)</TableHead>
            <TableHead>Estado</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(sources || []).map(s => (
            <TableRow key={s.id} data-testid={`editorial-source-row-${s.id}`}>
              <TableCell className="font-medium">{s.name}</TableCell>
              <TableCell className="text-xs text-textgray max-w-[260px] truncate">{s.main_url}</TableCell>
              <TableCell>
                <Select value={s.trust_level || "B"} onValueChange={(v) => setTrust(s.id, v)}>
                  <SelectTrigger className="w-24" data-testid={`editorial-source-trust-${s.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">A</SelectItem>
                    <SelectItem value="B">B</SelectItem>
                    <SelectItem value="C">C</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Input type="number" defaultValue={s.scrape_interval_minutes ?? 60}
                        className="w-24 font-mono"
                        onBlur={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (!isNaN(val) && val !== s.scrape_interval_minutes) {
                            setInterval(s.id, val, s.trust_level || "B");
                          }
                        }}
                        data-testid={`editorial-source-interval-${s.id}`} />
              </TableCell>
              <TableCell><span className="text-xs">{s.status}</span></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {updating && <div className="p-2 text-xs text-textgray">Actualizando…</div>}
    </div>
  );
}

// ---------------- AUDIT TAB ---------------------------------
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminEditorialDecisionLog({ limit: 100 });
      setRows(res.items || []);
    } catch { toast.error("No pudimos cargar el log"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-2" data-testid="editorial-audit-tab">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="editorial-audit-reload">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Recargar</span>
        </Button>
      </div>
      <div className="border border-softblue overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Acción</TableHead>
              <TableHead>Candidata</TableHead>
              <TableHead>Score</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead>Actor</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-textgray">Sin decisiones registradas.</TableCell></TableRow>
            )}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell className="text-[11px] text-textgray whitespace-nowrap">{r.created_at?.slice(0, 19).replace("T", " ")}</TableCell>
                <TableCell className="text-xs font-mono">{r.action}</TableCell>
                <TableCell className="text-[11px] text-textgray truncate max-w-[200px]" title={r.candidate_id}>{r.candidate_id}</TableCell>
                <TableCell className="font-mono text-xs">{r.score ?? "—"}</TableCell>
                <TableCell><StatusBadge status={r.new_status} /></TableCell>
                <TableCell className="text-xs text-textgray truncate max-w-[220px]" title={r.decision_reason}>{r.decision_reason}</TableCell>
                <TableCell className="text-[11px]">{r.actor}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------- SIMULATOR TAB ---------------------------------
function SimulatorTab({ locations, categories }) {
  const [target, setTarget] = useState(10);
  const [maxCount, setMaxCount] = useState(15);
  const [hoursWindow, setHoursWindow] = useState(48);
  const [useAI, setUseAI] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [detail, setDetail] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const locName = useCallback((id) => (locations.find(l => l.id === id) || {}).name || id || "—", [locations]);
  const catName = useCallback((slug) => (categories.find(c => c.slug === slug) || {}).name || slug || "—", [categories]);

  const runSim = async (confirm = false) => {
    setRunning(true);
    setResult(null);
    try {
      const res = await api.adminEditorialSimulate({
        target_count: Number(target) || 10,
        max_count: Number(maxCount) || 15,
        hours_window: Number(hoursWindow) || 48,
        use_ai: useAI,
        confirm,
      });
      if (res.needs_confirmation) {
        const left = res.budget_remaining_usd != null ? ` Presupuesto de relevancia restante: US$ ${res.budget_remaining_usd.toFixed(2)}.` : "";
        if (window.confirm(`${res.message}${left}\n\nSi se agota el presupuesto, el resto se evalúa con heurística.`)) {
          return runSim(true);
        }
        toast.info("Simulación cancelada. Podés correrla sin IA (heurística).");
        return;
      }
      setResult(res);
      toast.success(`Simulación completa (${Math.round((res.elapsed_ms || 0) / 1000)}s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la simulación");
    } finally { setRunning(false); }
  };

  const seedExtended = async () => {
    setSeeding(true);
    try {
      const res = await api.adminEditorialSeedExtended();
      toast.success(`Seed cargado: ${res.seeded} candidatas de prueba`);
    } catch (e) {
      toast.error("No pudimos cargar el seed extendido");
    } finally { setSeeding(false); }
  };

  const renderRow = (r, group) => {
    const badgeColor =
      group === "selected" ? "bg-emerald-100 text-emerald-800" :
      group === "secondary" ? "bg-amber-100 text-amber-800" :
      "bg-slate-100 text-slate-600";
    return (
      <TableRow key={r.candidate_id + group} data-testid={`sim-row-${group}-${r.candidate_id}`}
                className="cursor-pointer hover:bg-lightgray/40" onClick={() => setDetail(r)}>
        <TableCell className="font-mono text-xs w-10">{r.position || "—"}</TableCell>
        <TableCell className="max-w-[300px] truncate" title={r.title}>{r.title}</TableCell>
        <TableCell className="text-xs">{locName(r.location_id)}</TableCell>
        <TableCell className="text-xs">{catName(r.category_slug)}</TableCell>
        <TableCell>
          <span className={`px-2 py-0.5 text-[11px] font-bold ${TRUST_COLORS[r.source_level] || TRUST_COLORS.B}`}>
            {r.source_level || "B"}
          </span>
        </TableCell>
        <TableCell><ScoreBar value={r.total_score} /></TableCell>
        <TableCell className="text-xs font-mono">{r.relevance_score ?? "—"}/25</TableCell>
        <TableCell className="text-xs font-mono">{r.impact_score ?? "—"}/15</TableCell>
        <TableCell className="text-xs">{r.freshness_bucket || "—"}</TableCell>
        <TableCell>
          <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest ${badgeColor}`}>
            {group === "selected" ? "Selecc." : group === "secondary" ? "Secund." : "Desc."}
          </span>
        </TableCell>
        <TableCell className="text-[10px] text-textgray max-w-[220px] truncate" title={r.selection_reason}>
          {r.selection_reason}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-4" data-testid="editorial-simulator-tab">
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Play className="w-4 h-4" /> Parámetros de simulación</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-5 gap-3 items-end">
          <label className="text-xs text-textgray">Cantidad objetivo
            <Input type="number" value={target} onChange={(e) => setTarget(e.target.value)} min={1} max={50} data-testid="sim-target" />
          </label>
          <label className="text-xs text-textgray">Máximo
            <Input type="number" value={maxCount} onChange={(e) => setMaxCount(e.target.value)} min={1} max={50} data-testid="sim-max" />
          </label>
          <label className="text-xs text-textgray">Ventana (horas)
            <Input type="number" value={hoursWindow} onChange={(e) => setHoursWindow(e.target.value)} min={1} max={168} data-testid="sim-hours" />
          </label>
          <label className="text-xs text-textgray flex items-center gap-2 mt-4">
            <input type="checkbox" checked={useAI} onChange={(e) => setUseAI(e.target.checked)} data-testid="sim-use-ai" />
            Usar IA (Claude) para relevancia — consume presupuesto; pide confirmación si supera 20 llamadas
          </label>
          <div className="flex gap-2">
            <Button variant="outline" onClick={seedExtended} disabled={seeding} data-testid="sim-seed-extended">
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Seed extendido
            </Button>
            <Button onClick={() => runSim(false)} disabled={running} data-testid="sim-run">
              {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Simular
            </Button>
          </div>
        </CardContent>
      </Card>

      {running && (
        <div className="text-sm text-textgray flex items-center gap-2 py-3">
          <Loader2 className="w-4 h-4 animate-spin" />
          Corriendo simulación… con IA puede tardar 10-40s.
        </div>
      )}

      {result && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Resultado <span className="text-textgray font-mono text-xs ml-2">
                cfg_v{result.config_version_used} · {result.candidates_input} candidatas · {Math.round(result.elapsed_ms / 1000)}s · IA OK: {result.ai_calls_ok}/{result.candidates_input}
              </span></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-textgray grid md:grid-cols-4 gap-2 mb-3">
                <span>Seleccionadas: <b className="text-emerald-700">{result.stats?.selected}</b></span>
                <span>Secundarias: <b className="text-amber-700">{result.stats?.secondary}</b></span>
                <span>Descartadas: <b className="text-slate-700">{result.stats?.discarded}</b></span>
                <span>NAC+INT: <b>{result.stats?.national_international_selected}/{result.stats?.national_international_cap}</b></span>
              </div>
              <div className="border border-softblue overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Noticia</TableHead>
                      <TableHead>Ciudad</TableHead>
                      <TableHead>Categoría</TableHead>
                      <TableHead>Fte</TableHead>
                      <TableHead className="w-32">Score</TableHead>
                      <TableHead>Relev.</TableHead>
                      <TableHead>Impacto</TableHead>
                      <TableHead>Actualidad</TableHead>
                      <TableHead>Resultado</TableHead>
                      <TableHead>Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(result.selected || []).map(r => renderRow(r, "selected"))}
                    {(result.secondary || []).map(r => renderRow(r, "secondary"))}
                    {(result.discarded || []).slice(0, 15).map(r => renderRow(r, "discarded"))}
                    {(result.discarded || []).length > 15 && (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center text-xs text-textgray py-2">
                          … y {(result.discarded || []).length - 15} candidatas descartadas más
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl" data-testid="sim-breakdown-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-base">{detail?.title}</DialogTitle>
            <DialogDescription>Desglose de score y señales de IA</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline"><MapPin className="w-3 h-3 mr-1" />{locName(detail.location_id)}</Badge>
                <Badge variant="outline"><Tag className="w-3 h-3 mr-1" />{catName(detail.category_slug)}</Badge>
                <span className={`px-2 py-0.5 text-[11px] font-bold ${TRUST_COLORS[detail.source_level] || TRUST_COLORS.B}`}>Fuente {detail.source_level}</span>
                <span>Score total: <b className="font-mono">{detail.total_score}</b></span>
              </div>
              {detail.breakdown && (
                <div className="bg-lightgray/60 p-3 border border-softblue">
                  <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-1">Breakdown normalizado (0-100)</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                    {Object.entries(detail.breakdown).map(([k, v]) => (
                      <div key={k} className="flex justify-between">
                        <span className="text-textgray">{k}</span>
                        <span className={Number(v) < 0 ? "text-red-600" : "text-emerald-700"}>{v > 0 ? `+${v}` : v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-softblue p-2">
                  <div className="text-[10px] uppercase tracking-widest text-textgray">Relevancia</div>
                  <div className="font-mono text-lg font-bold">{detail.relevance_score ?? "—"}/25</div>
                </div>
                <div className="border border-softblue p-2">
                  <div className="text-[10px] uppercase tracking-widest text-textgray">Impacto</div>
                  <div className="font-mono text-lg font-bold">{detail.impact_score ?? "—"}/15</div>
                </div>
                <div className="border border-softblue p-2">
                  <div className="text-[10px] uppercase tracking-widest text-textgray">IA</div>
                  <div className="font-mono text-lg font-bold">{detail.ai_used ? "✓" : "—"}</div>
                </div>
              </div>
              <div className="text-xs italic text-textgray">Motivo: {detail.selection_reason}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------------- PANEL TAB (Etapa 3 §70) ---------------------------------
const MODE_LABELS = {
  automatic:  { label: "Automático (publica en portal)", color: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: PlayCircle },
  portal_only: { label: "Solo portal", color: "bg-amber-100 text-amber-800 border-amber-300", icon: PlayCircle },
  paused:     { label: "Pausado (solo analiza)", color: "bg-slate-200 text-slate-700 border-slate-300", icon: Pause },
};

function PanelTab() {
  const [panel, setPanel] = useState(null);
  const [nextRun, setNextRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [mode, setMode] = useState("paused");
  const [changingMode, setChangingMode] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, n] = await Promise.all([api.adminEditorialPanel(), api.adminEditorialNextRun()]);
      setPanel(p);
      setNextRun(n);
      setMode(p.automation_mode || "paused");
    } catch { toast.error("No pudimos cargar el panel"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeMode = async (newMode) => {
    setChangingMode(true);
    try {
      await api.adminEditorialAutomationModeSet(newMode);
      toast.success(`Modo cambiado a ${newMode}`);
      load();
    } catch { toast.error("No pudimos cambiar el modo"); }
    finally { setChangingMode(false); }
  };

  const triggerRun = async (runType) => {
    setRunning(runType);
    try {
      const r = await api.adminEditorialRunTrigger({
        run_type: runType, target_count: 10, max_count: 15,
      });
      toast.success(`Run ${runType}: ${r.normal_published + r.urgents_published} publicadas, ${r.ai_calls} llamadas IA, ${r.ai_cache_hits} cache hits`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error ejecutando run");
    } finally { setRunning(null); }
  };

  const sweepUnresolved = async () => {
    try {
      const r = await api.adminEditorialSweepUnresolved();
      toast.success(`${r.expired} candidatas no resueltas expiradas`);
      load();
    } catch { toast.error("Error al barrer no resueltas"); }
  };

  if (loading) return <div className="text-textgray text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando panel…</div>;
  if (!panel) return null;

  const ModeIcon = (MODE_LABELS[mode] || MODE_LABELS.paused).icon;
  const modeStyle = (MODE_LABELS[mode] || MODE_LABELS.paused);

  return (
    <div className="space-y-4" data-testid="editorial-panel-tab">
      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><LayoutDashboard className="w-4 h-4" /> Modo del motor</CardTitle></CardHeader>
        <CardContent className="flex items-center justify-between gap-3 flex-wrap">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 border ${modeStyle.color}`}>
            <ModeIcon className="w-4 h-4" />
            <span className="text-sm font-semibold">{modeStyle.label}</span>
          </div>
          <div className="flex gap-2">
            {Object.keys(MODE_LABELS).map(m => (
              <Button key={m} size="sm" variant={m === mode ? "default" : "outline"}
                        disabled={changingMode || m === mode}
                        onClick={() => changeMode(m)}
                        data-testid={`editorial-mode-${m}`}>
                {m}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4">
            <div className="text-[10px] uppercase tracking-widest text-textgray">Detectadas hoy</div>
            <div className="font-mono text-2xl font-bold text-blueblack">{panel.today?.detected}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-[10px] uppercase tracking-widest text-textgray">Publicadas hoy</div>
            <div className="font-mono text-2xl font-bold text-emerald-700">{panel.today?.total_published}
              <span className="text-textgray text-xs ml-1">/ {panel.today?.daily_hard_cap}</span>
            </div>
            <div className="text-[11px] text-textgray">Normales {panel.today?.normal_published} · Urgentes {panel.today?.urgent_published}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-[10px] uppercase tracking-widest text-textgray">Consumo IA hoy</div>
            <div className="font-mono text-2xl font-bold text-amber-700">{panel.ai?.calls}</div>
            <div className="text-[11px] text-textgray">
              {panel.ai?.pct_without_ai}% sin IA · {panel.ai?.cache_hits} cache hits
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-[10px] uppercase tracking-widest text-textgray">Próxima tanda</div>
            <div className="font-mono text-base font-bold text-blueblack">{nextRun?.run_type}</div>
            <div className="text-[11px] text-textgray">
              {nextRun?.at ? new Date(nextRun.at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" }) : "—"}
              <br />{nextRun?.eligible_candidates} elegibles de {nextRun?.queued_candidates}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">Estados de candidatas hoy</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2" data-testid="editorial-by-status">
          {Object.keys(panel.today?.by_status || {}).length === 0 && (
            <span className="text-xs text-textgray italic" data-testid="editorial-by-status-empty">Sin candidatas detectadas todavía en el día de hoy</span>
          )}
          {Object.entries(panel.today?.by_status || {}).map(([status, n]) => (
            <span key={status} className={`inline-flex items-center px-2 py-1 text-xs border ${STATUS_COLORS[status] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
              <b className="mr-1">{n}</b> {status}
            </span>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Zap className="w-4 h-4" /> Ejecución manual</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {["morning", "afternoon", "evening", "urgent", "manual"].map(rt => (
            <Button key={rt} size="sm" variant="outline"
                      disabled={running !== null}
                      onClick={() => triggerRun(rt)}
                      data-testid={`editorial-trigger-${rt}`}>
              {running === rt ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Play className="w-3 h-3 mr-1" />}
              Tanda {rt}
            </Button>
          ))}
          <div className="flex-1" />
          <Button size="sm" variant="ghost" onClick={sweepUnresolved} data-testid="editorial-sweep-unresolved">
            <AlertCircle className="w-3 h-3 mr-1" /> Expirar no resueltas
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> Últimas tandas</CardTitle></CardHeader>
        <CardContent>
          <div className="border border-softblue overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tanda</TableHead>
                  <TableHead>Inicio</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Candidatas</TableHead>
                  <TableHead>Publicadas</TableHead>
                  <TableHead>IA</TableHead>
                  <TableHead>Cache</TableHead>
                  <TableHead>Duración</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(panel.recent_runs || []).length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-4 text-textgray">Sin tandas recientes</TableCell></TableRow>
                )}
                {(panel.recent_runs || []).map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium text-xs">{r.run_type}</TableCell>
                    <TableCell className="text-[11px] text-textgray">{r.started_at?.slice(0, 19).replace("T", " ")}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                    <TableCell className="font-mono text-xs">{r.candidate_count}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <b className="text-emerald-700">{r.published_count || 0}</b>
                      {r.urgent_published_count ? <span className="text-red-600 text-[10px] ml-1">({r.urgent_published_count} urg)</span> : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-amber-700">{r.ai_calls || 0}</TableCell>
                    <TableCell className="font-mono text-xs text-emerald-700">{r.ai_cache_hits || 0}</TableCell>
                    <TableCell className="font-mono text-[11px]">{r.duration_ms ? Math.round(r.duration_ms / 1000) + "s" : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------- EVENTS TAB (Etapa 4) -----------------------------------
function EventsTab({ locations, categories }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminEditorialEvents({ limit: 50 });
      setEvents(res.items || []);
    } catch { toast.error("No pudimos cargar eventos"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setDetail(id); setDetailData(null);
    try { setDetailData(await api.adminEditorialEventDetail(id)); }
    catch { toast.error("No pudimos cargar el detalle"); }
  };

  const locName = (id) => (locations.find(l => l.id === id) || {}).name || id || "—";
  const catName = (id) => (categories.find(c => c.id === id) || {}).name || id || "—";

  return (
    <div className="space-y-3" data-testid="editorial-events-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-textgray">{events.length} eventos</div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="events-reload">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1">Recargar</span>
        </Button>
      </div>
      <div className="border border-softblue overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Título normalizado</TableHead>
              <TableHead>Ubicación</TableHead>
              <TableHead>Categoría</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Fuentes</TableHead>
              <TableHead>Candidatas</TableHead>
              <TableHead>Publicaciones</TableHead>
              <TableHead>Última actualización</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 && !loading && (
              <TableRow><TableCell colSpan={8} className="text-center py-4 text-textgray">Sin eventos aún</TableCell></TableRow>
            )}
            {events.map(ev => (
              <TableRow key={ev.id} className="cursor-pointer hover:bg-lightgray/40"
                          onClick={() => openDetail(ev.id)} data-testid={`event-row-${ev.id}`}>
                <TableCell className="font-medium text-sm max-w-[280px] truncate" title={ev.normalized_topic}>{ev.normalized_topic}</TableCell>
                <TableCell className="text-xs">{locName(ev.primary_location_id)}</TableCell>
                <TableCell className="text-xs">{catName(ev.main_category_id)}</TableCell>
                <TableCell><StatusBadge status={ev.event_status} /></TableCell>
                <TableCell className="font-mono text-xs">{ev.source_count || 0}</TableCell>
                <TableCell className="font-mono text-xs">{ev.candidate_count || 0}</TableCell>
                <TableCell className="font-mono text-xs">{ev.published_count || 0}</TableCell>
                <TableCell className="text-[11px] text-textgray">{ev.last_detected_at?.slice(0, 19).replace("T", " ")}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && (setDetail(null), setDetailData(null))}>
        <DialogContent className="max-w-3xl" data-testid="event-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">{detailData?.event?.normalized_topic || "Detalle de evento"}</DialogTitle>
            <DialogDescription>Fuentes, candidatas, artículos publicados y conflictos</DialogDescription>
          </DialogHeader>
          {detailData ? (
            <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline">{locName(detailData.event.primary_location_id)}</Badge>
                <Badge variant="outline">{catName(detailData.event.main_category_id)}</Badge>
                <StatusBadge status={detailData.event.event_status} />
                <span>Fuentes: <b>{detailData.event.source_count}</b></span>
                <span>Candidatas: <b>{detailData.event.candidate_count}</b></span>
                <span>Publicaciones: <b>{detailData.event.published_count}</b></span>
              </div>
              {detailData.event.event_facts && Object.keys(detailData.event.event_facts).length > 0 && (
                <div className="bg-lightgray/60 p-3 border border-softblue">
                  <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-1">Hechos consolidados</div>
                  <pre className="text-[11px] font-mono overflow-x-auto">{JSON.stringify(detailData.event.event_facts, null, 2).slice(0, 1500)}</pre>
                </div>
              )}
              {detailData.conflicts?.length > 0 && (
                <div className="border-l-4 border-red-500 pl-3">
                  <div className="text-xs font-bold text-red-700">Conflictos ({detailData.conflicts.length})</div>
                  {detailData.conflicts.slice(0, 3).map((c, i) => (
                    <div key={i} className="text-[11px] text-textgray">
                      <b>{c.field}</b>: {c.values?.map(v => `${v.value} (${v.source_ids?.length || v.sources?.length || 0} fuentes)`).join(" vs ")}
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-1">Candidatas ({detailData.candidates?.length || 0})</div>
                <div className="space-y-1">
                  {(detailData.candidates || []).map(c => (
                    <div key={c.id} className="border border-softblue p-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] px-1 border">{c.event_role || "—"}</span>
                        <span className="truncate">{c.generated_title || c.original_title}</span>
                      </div>
                      <div className="text-[10px] text-textgray mt-1">
                        {c.source_name} · novelty {c.novelty_score ?? "—"} · score {c.editorial_score ?? "—"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {detailData.articles?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-textgray font-semibold mb-1">Publicaciones ({detailData.articles.length})</div>
                  {detailData.articles.map(a => (
                    <div key={a.id} className="text-xs border border-softblue p-2">
                      {a.urgent && <span className="text-red-600 font-bold mr-1">URGENTE</span>}
                      {a.title} <span className="text-textgray text-[10px]">— {a.published_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-textgray text-sm flex items-center gap-2 py-6"><Loader2 className="w-4 h-4 animate-spin" /> Cargando detalle…</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => (setDetail(null), setDetailData(null))}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// =========================================================
// Etapa 5 — Distribución Multicanal
// =========================================================
const PLATFORM_LABEL = { facebook: "Facebook", instagram: "Instagram" };
const STATUS_STYLE = {
  queued:       "bg-blue-100 text-blue-800 border-blue-300",
  publishing:   "bg-amber-100 text-amber-800 border-amber-300",
  published:    "bg-emerald-100 text-emerald-800 border-emerald-300",
  test_mode:    "bg-cyan-100 text-cyan-800 border-cyan-300",
  retrying:     "bg-orange-100 text-orange-800 border-orange-300",
  failed:       "bg-red-100 text-red-800 border-red-300",
  disconnected: "bg-purple-100 text-purple-800 border-purple-300",
  expired:      "bg-slate-200 text-slate-700 border-slate-300",
};

function ChannelMeter({ label, used, target, max, color }) {
  const pctToTarget = Math.min(100, Math.round((used / Math.max(1, target)) * 100));
  const pctToMax = Math.min(100, Math.round((used / Math.max(1, max)) * 100));
  const bar = pctToMax >= 100 ? "bg-red-500" : pctToTarget >= 100 ? "bg-emerald-500" : color;
  return (
    <div className="space-y-1" data-testid={`distribution-meter-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium">{label}</span>
        <span className="font-mono tabular-nums">
          {used} / <span className="text-textgray">{target}</span> · máx {max}
        </span>
      </div>
      <div className="h-2 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full ${bar} transition-all`} style={{ width: `${pctToMax}%` }} />
      </div>
    </div>
  );
}

function DistributionTab() {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [s, q] = await Promise.all([
        api.adminEditorialDistributionStatus(),
        api.adminEditorialDistributionQueue({ limit: 50 }),
      ]);
      setStatus(s);
      setConfig(s.config);
      setQueue(Array.isArray(q) ? q : []);
    } catch { toast.error("No pudimos cargar la distribución"); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(() => load({ silent: true }), 15000);
    return () => clearInterval(t);
  }, [load]);

  const patchField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const patchSlot = (slot, ch, value) => setConfig((c) => ({
    ...c, slot_targets: { ...(c.slot_targets || {}), [slot]: { ...((c.slot_targets || {})[slot] || {}), [ch]: value } },
  }));

  const saveConfig = async () => {
    setSaving(true);
    try {
      const patch = {
        facebook_enabled: !!config.facebook_enabled,
        instagram_enabled: !!config.instagram_enabled,
        facebook_daily_target: Number(config.facebook_daily_target),
        facebook_daily_max: Number(config.facebook_daily_max),
        instagram_daily_target: Number(config.instagram_daily_target),
        instagram_daily_max: Number(config.instagram_daily_max),
        slot_targets: config.slot_targets,
        facebook_publish_interval_minutes: Number(config.facebook_publish_interval_minutes),
        instagram_publish_interval_minutes: Number(config.instagram_publish_interval_minutes),
        max_retries: Number(config.max_retries),
        social_ttl_hours: Number(config.social_ttl_hours),
        facebook_min_score: Number(config.facebook_min_score),
        instagram_min_score: Number(config.instagram_min_score),
      };
      const saved = await api.adminEditorialDistributionConfigPut(patch);
      setConfig(saved);
      toast.success("Configuración de distribución guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando configuración");
    } finally { setSaving(false); }
  };

  const processQueue = async () => {
    setProcessing(true);
    try {
      const r = await api.adminEditorialDistributionProcess(20);
      toast.success(`Cola procesada: ${r.processed} ítem(s)`);
      load({ silent: true });
    } catch { toast.error("Error procesando cola"); }
    finally { setProcessing(false); }
  };

  const retryOne = async (id) => {
    try {
      await api.adminEditorialDistributionRetry(id);
      toast.success("Reintentando publicación");
      load({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reintentar");
    }
  };

  if (loading) return <div className="text-textgray text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando distribución…</div>;
  if (!status || !config) return null;

  const c = status.counters;
  const meta = status.meta || {};

  return (
    <div className="space-y-4" data-testid="editorial-distribution-tab">
      {meta.test_mode && (
        <div className="text-xs px-3 py-2 border border-cyan-300 bg-cyan-50 text-cyan-900 rounded">
          <strong>Modo prueba activo</strong> — las publicaciones no llegan a Meta.
        </div>
      )}
      {status.automation_mode !== "automatic" && (
        <div className="text-xs px-3 py-2 border border-amber-300 bg-amber-50 text-amber-900 rounded">
          El modo del motor es <strong>{status.automation_mode}</strong>. Facebook e Instagram
          sólo se distribuyen en modo <strong>automatic</strong>.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Gauge className="w-4 h-4" /> Contadores del día · franja actual: <Badge>{c.current_slot}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ChannelMeter label="Facebook (hoy)"
            used={c.today.facebook} target={config.facebook_daily_target}
            max={config.facebook_daily_max} color="bg-blue-500" />
          <ChannelMeter label="Instagram (hoy)"
            used={c.today.instagram} target={config.instagram_daily_target}
            max={config.instagram_daily_max} color="bg-pink-500" />
          <div className="text-xs text-textgray">
            <div className="flex items-center justify-between">
              <span>En cola FB: <strong className="text-blueblack">{c.queued.facebook}</strong></span>
              <span>En cola IG: <strong className="text-blueblack">{c.queued.instagram}</strong></span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span>Fallidas hoy FB: <strong className="text-red-600">{c.failed_today.facebook}</strong></span>
              <span>Fallidas hoy IG: <strong className="text-red-600">{c.failed_today.instagram}</strong></span>
            </div>
          </div>
          <div className="text-xs text-textgray">
            <div>Franja actual — target FB: <strong>{(config.slot_targets?.[c.current_slot]?.facebook) ?? "–"}</strong> · IG: <strong>{(config.slot_targets?.[c.current_slot]?.instagram) ?? "–"}</strong></div>
            <div className="mt-1">Publicaciones exitosas en la franja — FB: <strong>{c.slot.facebook}</strong> · IG: <strong>{c.slot.instagram}</strong></div>
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={processQueue} disabled={processing}
                data-testid="distribution-process-btn">
                {processing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                <span className="ml-1">Procesar cola ahora</span>
              </Button>
              <Button size="sm" variant="outline" onClick={() => load({ silent: true })}
                data-testid="distribution-refresh-btn">
                <RefreshCw className="w-3.5 h-3.5" /><span className="ml-1">Actualizar</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" /> Configuración de canales</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2 border border-slate-200 rounded p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Facebook</span>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={!!config.facebook_enabled}
                    onChange={(e) => patchField("facebook_enabled", e.target.checked)}
                    data-testid="distribution-fb-enabled" />
                  Activo
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label>Objetivo diario
                  <Input type="number" min={0} max={200} value={config.facebook_daily_target}
                    onChange={(e) => patchField("facebook_daily_target", e.target.value)}
                    data-testid="distribution-fb-target" />
                </label>
                <label>Máximo diario
                  <Input type="number" min={0} max={200} value={config.facebook_daily_max}
                    onChange={(e) => patchField("facebook_daily_max", e.target.value)}
                    data-testid="distribution-fb-max" />
                </label>
                <label>Score mínimo
                  <Input type="number" min={0} max={100} value={config.facebook_min_score}
                    onChange={(e) => patchField("facebook_min_score", e.target.value)}
                    data-testid="distribution-fb-min-score" />
                </label>
                <label>Intervalo entre posts (min)
                  <Input type="number" min={0} max={60} value={config.facebook_publish_interval_minutes}
                    onChange={(e) => patchField("facebook_publish_interval_minutes", e.target.value)}
                    data-testid="distribution-fb-interval" />
                </label>
              </div>
            </div>

            <div className="space-y-2 border border-slate-200 rounded p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">Instagram</span>
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={!!config.instagram_enabled}
                    onChange={(e) => patchField("instagram_enabled", e.target.checked)}
                    data-testid="distribution-ig-enabled" />
                  Activo
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label>Objetivo diario
                  <Input type="number" min={0} max={200} value={config.instagram_daily_target}
                    onChange={(e) => patchField("instagram_daily_target", e.target.value)}
                    data-testid="distribution-ig-target" />
                </label>
                <label>Máximo diario
                  <Input type="number" min={0} max={200} value={config.instagram_daily_max}
                    onChange={(e) => patchField("instagram_daily_max", e.target.value)}
                    data-testid="distribution-ig-max" />
                </label>
                <label>Score mínimo
                  <Input type="number" min={0} max={100} value={config.instagram_min_score}
                    onChange={(e) => patchField("instagram_min_score", e.target.value)}
                    data-testid="distribution-ig-min-score" />
                </label>
                <label>Intervalo entre posts (min)
                  <Input type="number" min={0} max={60} value={config.instagram_publish_interval_minutes}
                    onChange={(e) => patchField("instagram_publish_interval_minutes", e.target.value)}
                    data-testid="distribution-ig-interval" />
                </label>
              </div>
            </div>
          </div>

          <div className="border border-slate-200 rounded p-3">
            <div className="text-sm font-medium mb-2">Objetivos por franja (SOFT, no mínimos)</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              {["morning", "afternoon", "evening"].map((slot) => (
                <div key={slot} className="border border-slate-100 rounded p-2">
                  <div className="font-medium capitalize mb-1">{slot === "morning" ? "08:00 · Mañana" : slot === "afternoon" ? "15:00 · Tarde" : "20:00 · Noche"}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <label>FB
                      <Input type="number" min={0} max={50}
                        value={(config.slot_targets?.[slot]?.facebook) ?? 0}
                        onChange={(e) => patchSlot(slot, "facebook", Number(e.target.value))}
                        data-testid={`distribution-slot-${slot}-fb`} />
                    </label>
                    <label>IG
                      <Input type="number" min={0} max={50}
                        value={(config.slot_targets?.[slot]?.instagram) ?? 0}
                        onChange={(e) => patchSlot(slot, "instagram", Number(e.target.value))}
                        data-testid={`distribution-slot-${slot}-ig`} />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <label>Máx reintentos
              <Input type="number" min={0} max={10} value={config.max_retries}
                onChange={(e) => patchField("max_retries", e.target.value)}
                data-testid="distribution-max-retries" />
            </label>
            <label>TTL de cola (h)
              <Input type="number" min={1} max={72} value={config.social_ttl_hours}
                onChange={(e) => patchField("social_ttl_hours", e.target.value)}
                data-testid="distribution-ttl-hours" />
            </label>
            <div className="text-textgray flex items-end">Backoff: {(config.retry_backoff_minutes || []).join(" / ")} min</div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button onClick={saveConfig} disabled={saving} data-testid="distribution-save-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Guardar configuración
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Layers className="w-4 h-4" /> Cola de distribución (últimas 50)</CardTitle></CardHeader>
        <CardContent>
          {queue.length === 0 ? (
            <div className="text-xs text-textgray">No hay publicaciones en cola.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Canal</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Programado</TableHead>
                    <TableHead>Reintentos</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {queue.map((row) => (
                    <TableRow key={row.id} data-testid={`distribution-row-${row.id}`}>
                      <TableCell><Badge variant="outline">{PLATFORM_LABEL[row.platform] || row.platform}</Badge>{row.is_urgent && <Zap className="inline w-3 h-3 text-red-500 ml-1" />}</TableCell>
                      <TableCell><span className={`px-2 py-0.5 text-xs border rounded ${STATUS_STYLE[row.status] || "bg-slate-100"}`}>{row.status}</span></TableCell>
                      <TableCell className="font-mono text-xs">{row.channel_selection_score ?? "–"}</TableCell>
                      <TableCell className="text-xs">{row.selection_reason || "–"}{row.limit_override_reason ? ` · override:${row.limit_override_reason}` : ""}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{row.scheduled_at ? new Date(row.scheduled_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" }) : "–"}</TableCell>
                      <TableCell className="text-xs">{row.retries ?? 0}</TableCell>
                      <TableCell className="text-xs text-red-600 max-w-[180px] truncate" title={row.error_message}>{row.error_code || ""}</TableCell>
                      <TableCell className="text-right">
                        {["failed", "disconnected", "expired", "retrying"].includes(row.status) && (
                          <Button size="sm" variant="outline" onClick={() => retryOne(row.id)}
                            data-testid={`distribution-retry-${row.id}`}>
                            Reintentar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ---------------- MAIN PAGE ---------------------------------
export default function AdminEditorialEnginePage() {
  const [config, setConfig] = useState(null);
  const [locations, setLocations] = useState([]);
  const [sources, setSources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("panel");

  const loadAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setInitialLoading(true);
    try {
      const [cfg, locs, srcs, cats] = await Promise.all([
        api.adminEditorialConfigGet(),
        api.adminEditorialLocations(),
        api.adminEditorialSources(),
        // Reuse public categories endpoint (already available on the admin).
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`).then(r => r.json()),
      ]);
      setConfig(cfg);
      setLocations(locs || []);
      setSources(srcs || []);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (e) {
      toast.error("No pudimos inicializar el motor editorial");
    } finally { if (!silent) setInitialLoading(false); }
  }, []);

  // A silent refresh keeps the current tab and avoids the loading spinner flash.
  const silentRefresh = useCallback(() => loadAll({ silent: true }), [loadAll]);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <div className="space-y-6" data-testid="editorial-engine-page">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-turquoise/10 text-turquoise border border-turquoise/30">
          <BrainCircuit className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-serif text-2xl text-blueblack">Motor Editorial</h1>
          <p className="text-xs text-textgray">Etapa 5 — Distribución multicanal (Portal + Facebook + Instagram).</p>
        </div>
      </header>

      {initialLoading ? (
        <div className="text-textgray text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando motor editorial…</div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList data-testid="editorial-tabs">
            <TabsTrigger value="panel" data-testid="editorial-tab-panel">
              <LayoutDashboard className="w-3.5 h-3.5 mr-1" /> Panel
            </TabsTrigger>
            <TabsTrigger value="candidates" data-testid="editorial-tab-candidates">Candidatas</TabsTrigger>
            <TabsTrigger value="simulator" data-testid="editorial-tab-simulator">
              <Play className="w-3.5 h-3.5 mr-1" /> Simulador
            </TabsTrigger>
            <TabsTrigger value="config" data-testid="editorial-tab-config">Configuración</TabsTrigger>
            <TabsTrigger value="sources" data-testid="editorial-tab-sources">Fuentes A/B/C</TabsTrigger>
            <TabsTrigger value="events" data-testid="editorial-tab-events">
              <Layers className="w-3.5 h-3.5 mr-1" /> Eventos
            </TabsTrigger>
            <TabsTrigger value="distribution" data-testid="editorial-tab-distribution">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Distribución
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="editorial-tab-audit">
              <ScrollText className="w-3.5 h-3.5 mr-1" /> Auditoría
            </TabsTrigger>
          </TabsList>
          <TabsContent value="panel" className="pt-4">
            <PanelTab />
          </TabsContent>
          <TabsContent value="candidates" className="pt-4">
            <CandidatesTab locations={locations} sources={sources} categories={categories} />
          </TabsContent>
          <TabsContent value="simulator" className="pt-4">
            <SimulatorTab locations={locations} categories={categories} />
          </TabsContent>
          <TabsContent value="config" className="pt-4">
            <ConfigTab config={config} onSaved={silentRefresh} />
          </TabsContent>
          <TabsContent value="sources" className="pt-4">
            <SourcesTab sources={sources} onChanged={silentRefresh} />
          </TabsContent>
          <TabsContent value="events" className="pt-4">
            <EventsTab locations={locations} categories={categories} />
          </TabsContent>
          <TabsContent value="distribution" className="pt-4">
            <DistributionTab />
          </TabsContent>
          <TabsContent value="audit" className="pt-4">
            <AuditTab />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
